/**
 * Project Shell — wraps project pages with the project-aware Topbar.
 */
import { useState, useEffect } from "react";
import { useParams, Outlet } from "react-router-dom";
import { api } from "../lib/api.ts";
import { Topbar } from "../components/shared/Topbar.tsx";

export function ProjectShell() {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<any>(null);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [regStatus, setRegStatus] = useState<Awaited<ReturnType<typeof api.getRegulatoryStatus>> | null>(null);

  useEffect(() => {
    if (id) {
      api.getProject(id).then(setProject).catch(() => {});
      api.getProjectAlerts(id).then(setAlerts).catch(() => {});
      api.getRegulatoryStatus(id).then(setRegStatus).catch(() => {});
    }
  }, [id]);

  function handleDismissAlert(alertId: string) {
    if (!id) return;
    api.dismissAlert(id, alertId).then(() => {
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    }).catch(console.error);
  }

  function handleRenamed(newName: string) {
    setProject((prev: any) => prev ? { ...prev, name: newName } : prev);
  }

  // S4 header line: which verified dataset this project's requirements come
  // from, with an explicit staleness flag past the reliance-age policy.
  const ds = regStatus?.status;
  const ageDays = ds ? Math.floor((Date.now() - new Date(ds.applied_at + "Z").getTime()) / 86_400_000) : 0;
  const stale = ds != null && regStatus != null && ageDays > regStatus.max_reliance_age_days;

  return (
    <>
      <Topbar
        projectName={project?.name}
        projectId={id}
        countryCode={project?.country_code}
        alerts={alerts}
        onDismissAlert={handleDismissAlert}
        onProjectRenamed={handleRenamed}
      />
      {ds && (
        <div className="px-6 py-1 text-[12px] text-text-tertiary border-b border-border flex items-center gap-2">
          <span>Regulatory data: v{ds.version} · applied {ds.applied_at.slice(0, 10)}</span>
          {stale && (
            <span
              className="text-warning-text font-medium"
              title={`This dataset was applied ${ageDays} days ago — older than the ${regStatus!.max_reliance_age_days}-day reliance policy. Regulations may have changed since.`}
            >
              may be stale
            </span>
          )}
        </div>
      )}
      <Outlet />
    </>
  );
}
